import { authHeaders } from "./auth";
import { apiFetch } from "./backend";
import { useStore } from "./store";
import { activeSkillIds, buildSkillGate } from "./skill-gate";

export const inTauri = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

export type ToolSource = "web" | "tauri" | "server" | "mcp";

export type PermissionScope =
  | "fs_read"
  | "fs_write"
  | "shell_exec"
  | "network_fetch"
  | "subprocess";

export type ToolContext = { chatId: string | null };

export type Tool = {
  source: ToolSource;
  name: string;
  description?: string;
  schema: Record<string, unknown>;
  run: (args: Record<string, unknown>, ctx: ToolContext) => Promise<unknown>;
  requiresPermission?: PermissionScope;
  alwaysOn?: boolean;
};

export type OpenAITool = {
  type: "function";
  function: { name: string; description?: string; parameters: Record<string, unknown> };
};

export function isToolEnabled(name: string): boolean {
  return useStore.getState().settings.toolPrefs?.[name] === true;
}

export async function setToolEnabled(name: string, enabled: boolean) {
  const current = useStore.getState().settings.toolPrefs ?? {};
  const next = { ...current };
  if (enabled) next[name] = true;
  else delete next[name];
  await useStore.getState().updateSettings({ toolPrefs: next });
}

const sources: Array<() => Promise<Tool[]>> = [];

export function registerToolSource(load: () => Promise<Tool[]>) {
  sources.push(load);
}

export async function loadAllTools(): Promise<Tool[]> {
  const lists = await Promise.all(sources.map((s) => s().catch(() => [] as Tool[])));
  return lists.flat();
}

export async function loadTools(): Promise<Tool[]> {
  const all = await loadAllTools();
  const state = useStore.getState();
  const gate = buildSkillGate(
    state.skills,
    activeSkillIds(state.activePinnedSkillId, state.activeActivatedSkillIds),
    state.settings.toolPrefs ?? {},
  );
  return all.filter((t) => t.alwaysOn || gate.allows(t.name));
}

export function toOpenAIFormat(tools: Tool[]): OpenAITool[] {
  return tools.map((t) => ({
    type: "function",
    function: { name: t.name, description: t.description, parameters: t.schema },
  }));
}

registerToolSource(async () => {
  if (!inTauri) return [];
  const { invoke } = await import("@tauri-apps/api/core");
  return [
    {
      source: "tauri",
      name: "system_info",
      description: "Get the host OS, architecture, and hostname.",
      schema: { type: "object", properties: {}, additionalProperties: false },
      run: async () => invoke("system_info"),
    },
    {
      source: "tauri",
      name: "process_list",
      description:
        "List running processes with CPU%, memory (MB), and user. Includes load average and memory totals. Sorted by CPU by default.",
      schema: {
        type: "object",
        properties: {
          sort_by: { type: "string", enum: ["cpu", "mem", "name"], description: "Sort key (default cpu)." },
          limit: { type: "integer", description: "Max processes returned (default 50, max 500)." },
          name_filter: { type: "string", description: "Case-insensitive substring match on process name." },
        },
        additionalProperties: false,
      },
      run: async (args) => invoke("process_list", { args }),
    },
    {
      source: "tauri",
      name: "fs_list",
      description: "List entries in a directory. Path supports ~ for home. Capped at 500 entries.",
      requiresPermission: "fs_read",
      schema: {
        type: "object",
        properties: {
          path: { type: "string", description: "Directory path (e.g. '~', '~/Documents', '/tmp')." },
          show_hidden: { type: "boolean", description: "Include dotfiles (default false)." },
        },
        required: ["path"],
        additionalProperties: false,
      },
      run: async (args) => invoke("fs_list", { args }),
    },
    {
      source: "tauri",
      name: "fs_read",
      description:
        "Read a file's contents (text). Returns up to max_bytes (default 256KB, max 4MB). Binary files return a placeholder.",
      requiresPermission: "fs_read",
      schema: {
        type: "object",
        properties: {
          path: { type: "string" },
          max_bytes: { type: "integer", description: "Byte cap (default 262144, max 4194304)." },
        },
        required: ["path"],
        additionalProperties: false,
      },
      run: async (args) => invoke("fs_read", { args }),
    },
    {
      source: "tauri",
      name: "fs_search",
      description:
        "Recursively find files/dirs whose name matches a glob (e.g. '*.ts', 'README*'). Skips hidden by default.",
      requiresPermission: "fs_read",
      schema: {
        type: "object",
        properties: {
          root: { type: "string", description: "Directory to search under." },
          pattern: { type: "string", description: "Glob pattern matched against filename only." },
          max_depth: { type: "integer" },
          limit: { type: "integer", description: "Max hits (default 200, max 2000)." },
          show_hidden: { type: "boolean" },
        },
        required: ["root", "pattern"],
        additionalProperties: false,
      },
      run: async (args) => invoke("fs_search", { args }),
    },
    {
      source: "tauri",
      name: "fs_grep",
      requiresPermission: "fs_read",
      description:
        "Search file contents recursively for a substring. Skips binary and >4MB files. Capped at 100 matches by default.",
      schema: {
        type: "object",
        properties: {
          root: { type: "string" },
          query: { type: "string", description: "Substring to search for." },
          file_pattern: { type: "string", description: "Optional glob to restrict by filename (e.g. '*.rs')." },
          max_depth: { type: "integer" },
          limit: { type: "integer", description: "Max matches (default 100, max 1000)." },
          case_insensitive: { type: "boolean" },
          show_hidden: { type: "boolean" },
        },
        required: ["root", "query"],
        additionalProperties: false,
      },
      run: async (args) => invoke("fs_grep", { args }),
    },
  ];
});

registerToolSource(async () => {
  if (!inTauri) return [];
  const { invoke } = await import("@tauri-apps/api/core");
  let list: Array<{
    serverName: string;
    name: string;
    description?: string;
    inputSchema: Record<string, unknown>;
  }>;
  try {
    list = await invoke("local_mcp_tools");
  } catch {
    return [];
  }
  return list.map((t) => ({
    source: "tauri",
    name: `${t.serverName}__${t.name}`,
    description: t.description,
    schema: t.inputSchema,
    requiresPermission: "subprocess",
    run: async (args) => invoke("local_mcp_call", { server: t.serverName, tool: t.name, args }),
  }));
});

registerToolSource(async () => {
  const r = await apiFetch("/api/tools");
  if (!r.ok) return [];
  const list: Array<{ name: string; description: string; schema: Record<string, unknown> }> = await r.json();
  return list.map((t) => ({
    source: "server",
    name: t.name,
    description: t.description,
    schema: t.schema,
    run: async (args) => {
      const chatId = useStore.getState().activeId;
      const res = await apiFetch("/api/tools/call", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: t.name, args, chatId }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = body && typeof body === "object" && typeof (body as any).error === "string"
          ? (body as any).error
          : `Tool call failed: ${res.status}`;
        throw new Error(msg);
      }
      return body;
    },
  }));
});

registerToolSource(async () => {
  const r = await apiFetch("/api/mcp/tools");
  if (!r.ok) return [];
  const raw: Array<{
    name: string;
    description?: string;
    inputSchema: Record<string, unknown>;
    serverName: string;
  }> = await r.json();
  return raw.map((t) => ({
    source: "mcp",
    name: `${t.serverName}__${t.name}`,
    description: t.description,
    schema: t.inputSchema,
    run: async (args) => {
      const res = await apiFetch("/api/mcp/call", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ serverName: t.serverName, toolName: t.name, args }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = body && typeof body === "object" && typeof (body as any).error === "string"
          ? (body as any).error
          : `MCP call failed: ${res.status}`;
        throw new Error(msg);
      }
      return body;
    },
  }));
});
